import html2pdf from 'html2pdf.js';
import { marked } from 'marked';


export const markdownToPdf = async (markdownContent, options = {}) => {
  const {
    filename = 'document.pdf',
    pdfOptions = {},
    markedOptions = {},
  } = options;

  const defaultMarkedOptions = {
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false,
    ...markedOptions,
  };

  const defaultPdfOptions = {
    margin: [10, 10, 10, 10],
    filename: filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true,
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait',
    },
    ...pdfOptions,
  };

  try {
    marked.setOptions(defaultMarkedOptions);
    const htmlContent = marked.parse(markdownContent);

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    
    tempContainer.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      line-height: 1.6;
      color: #333;
      padding: 20px;
      max-width: 100%;
      word-wrap: break-word;
    `;

    const styles = `
      h1, h2, h3, h4, h5, h6 { 
        margin-top: 1.5em; 
        margin-bottom: 1em; 
        font-weight: 600; 
        line-height: 1.25;
        page-break-after: avoid;
      }
      h1 { font-size: 1.5em; border-bottom: 1px solid #e1e4e8; padding-bottom: 0.3em; }
      h2 { font-size: 1.25em; border-bottom: 1px solid #e1e4e8; padding-bottom: 0.3em; }
      h3 { font-size: 1.1em; }
      p { margin-top: 0; margin-bottom: 0.85em; font-size: 0.9em; }
      a { color: #0366d6; text-decoration: none; }
      ul, ol { margin-top: 0; margin-bottom: 0.85em; padding-left: 1.8em; }
      li { margin-bottom: 0.25em; }
      code {
        padding: 0.2em 0.4em;
        margin: 0;
        font-size: 0.8em;
        background-color: rgba(27, 31, 35, 0.05);
        border-radius: 3px;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
      }
      pre {
        padding: 0.85em;
        overflow: auto;
        font-size: 0.8em;
        line-height: 1.45;
        background-color: #f6f8fa;
        border-radius: 6px;
        margin-bottom: 0.85em;
        page-break-inside: avoid;
      }
      pre code {
        display: inline;
        padding: 0;
        margin: 0;
        background-color: transparent;
        border: 0;
      }
      blockquote {
        margin: 0 0 0.85em 0;
        padding: 0 0.9em;
        color: #6a737d;
        border-left: 0.25em solid #dfe2e5;
        page-break-inside: avoid;
        font-size: 0.9em;
      }
      table {
        border-spacing: 0;
        border-collapse: collapse;
        margin-bottom: 0.85em;
        width: 100%;
        page-break-inside: avoid;
        font-size: 0.85em;
      }
      table th {
        font-weight: 600;
        padding: 0.35em 0.7em;
        border: 1px solid #dfe2e5;
        background-color: #f6f8fa;
      }
      table td {
        padding: 0.35em 0.7em;
        border: 1px solid #dfe2e5;
      }
      hr {
        height: 0.25em;
        padding: 0;
        margin: 1.125em 0;
        background-color: #e1e4e8;
        border: 0;
      }
      img {
        max-width: 100%;
        box-sizing: content-box;
      }
      strong { font-weight: 600; }
      em { font-style: italic; }
      del { text-decoration: line-through; }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    tempContainer.insertBefore(styleElement, tempContainer.firstChild);

    await html2pdf().set(defaultPdfOptions).from(tempContainer).save();

    return { success: true };
  } catch (error) {
    console.error('Error converting markdown to PDF:', error);
    throw new Error(`Failed to convert markdown to PDF: ${error.message}`);
  }
};

export const markdownToPdfBlob = async (markdownContent, options = {}) => {
  const {
    pdfOptions = {},
    markedOptions = {},
  } = options;

  const defaultMarkedOptions = {
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false,
    ...markedOptions,
  };

  const defaultPdfOptions = {
    margin: [10, 10, 10, 10],
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true,
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait',
    },
    ...pdfOptions,
  };

  try {
    marked.setOptions(defaultMarkedOptions);
    const htmlContent = marked.parse(markdownContent);

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    
    tempContainer.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      line-height: 1.6;
      color: #333;
      padding: 20px;
      max-width: 100%;
      word-wrap: break-word;
    `;

    const pdf = await html2pdf().set(defaultPdfOptions).from(tempContainer).outputPdf('blob');
    
    return pdf;
  } catch (error) {
    console.error('Error converting markdown to PDF blob:', error);
    throw new Error(`Failed to convert markdown to PDF blob: ${error.message}`);
  }
};
